import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ReactSelect from 'react-select';
import dayjs from 'dayjs';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingButton from 'components/common/LoadingButton';
import { formatMoney, parseRouteParams } from 'helpers/utils';
import paths from 'routes/paths';
import { useDonors } from 'hooks/useDonors';
import { usePackages } from 'hooks/useDonationSettings';
import {
  useCreatePledge,
  useCreatePledgeFollowUp,
  useOrganisationStaff
} from 'hooks/usePledges';
import { useGetPaymentMethods } from 'hooks/useStripePayments';
import AddCardModal from 'components/common/AddCardModal';
import {
  PLEDGE_FREQUENCIES,
  PLEDGE_PRIORITIES,
  PLEDGE_SOURCES,
  CONTACT_METHODS
} from './pledgeConstants';

const AddPledge = () => {
  const navigate = useNavigate();
  const { donors } = useDonors();
  const { packages } = usePackages();
  const { staff } = useOrganisationStaff();
  const { createPledgeAsync, isCreating } = useCreatePledge();
  const { createFollowUpAsync } = useCreatePledgeFollowUp();

  // Card 1 — donor. New donors are created through the full Create Donor form
  // (it already captures contact details, lead source, gift aid, etc.).
  const [selectedDonor, setSelectedDonor] = useState(null);

  // Card 2 — pledge details
  const [packageId, setPackageId] = useState(null);
  const [amount, setAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [frequency, setFrequency] = useState('one_off');
  const [durationType, setDurationType] = useState('until_cancelled');
  const [installments, setInstallments] = useState('');

  // Card 3 — payment preference. 'contact' = staff follow up and record
  // payments manually; 'auto' = the donor's saved card is charged on each
  // due date by the process-pledge-payments daily job.
  const [paymentPreference, setPaymentPreference] = useState('contact');
  const [contactMethod, setContactMethod] = useState('phone');
  const [followUpDate, setFollowUpDate] = useState(
    dayjs().add(7, 'day').format('YYYY-MM-DD')
  );
  const [contactNotes, setContactNotes] = useState('');
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [giftAid, setGiftAid] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const { getPaymentMethods } = useGetPaymentMethods();

  const handleCardAdded = paymentMethod => {
    setSavedCards(prev => [...prev, paymentMethod]);
    setSelectedCardId(paymentMethod.id);
  };

  // Default the Gift Aid flag from the donor's declaration status
  useEffect(() => {
    setGiftAid(Boolean(selectedDonor?.donor?.gift_aid_elgibility));
  }, [selectedDonor]);

  // Load the donor's saved cards when auto payment is chosen
  useEffect(() => {
    setSavedCards([]);
    setSelectedCardId('');
    if (paymentPreference !== 'auto' || !selectedDonor?.value) return;

    let cancelled = false;
    setCardsLoading(true);
    getPaymentMethods(selectedDonor.value)
      .then(result => {
        if (cancelled) return;
        const cards = result?.payment_methods || [];
        setSavedCards(cards);
        setSelectedCardId(
          result?.default_payment_method_id || cards[0]?.id || ''
        );
      })
      .catch(() => {
        if (!cancelled) setSavedCards([]);
      })
      .finally(() => {
        if (!cancelled) setCardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentPreference, selectedDonor?.value]);

  // Card 4 — internal tracking
  const [assignedTo, setAssignedTo] = useState(null);
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  // Where the pledge came from; event pledges also record the event itself
  const [source, setSource] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventDate, setEventDate] = useState('');

  const donorOptions = useMemo(
    () =>
      (donors || []).map(d => ({
        value: d.id,
        label: `${d.first_name} ${d.last_name}${
          d.primary_email ? ` — ${d.primary_email}` : ''
        }`,
        donor: d
      })),
    [donors]
  );

  const packageOptions = useMemo(
    () => (packages || []).map(p => ({ value: p.id, label: p.package_title })),
    [packages]
  );

  // Selected campaign's preset amounts (same source as the donation form).
  // 'custom' in the package's amount list allows a free amount.
  const selectedPackageData = useMemo(
    () => (packages || []).find(p => p.id === packageId?.value),
    [packages, packageId]
  );
  const packagePresetAmounts = useMemo(
    () =>
      (selectedPackageData?.donation_amount || []).filter(a => a !== 'custom'),
    [selectedPackageData]
  );
  const packageAllowsCustom = (
    selectedPackageData?.donation_amount || []
  ).includes('custom');

  const handleSelectPackage = option => {
    setPackageId(option);
    // Campaign changed: previous amount may not be valid for it
    setAmount('');
    setUseCustomAmount(false);
  };

  const staffOptions = useMemo(
    () =>
      (staff || []).map(s => ({
        value: s.id,
        label: `${s.first_name} ${s.last_name}`
      })),
    [staff]
  );

  const effectiveInstallments =
    frequency === 'one_off'
      ? 1
      : durationType === 'fixed'
      ? parseInt(installments, 10) || 0
      : null;

  const totalValue =
    amount && effectiveInstallments
      ? Number(amount) * effectiveInstallments
      : null;

  const isAuto = paymentPreference === 'auto';

  const isValid =
    selectedDonor &&
    Number(amount) > 0 &&
    (durationType !== 'fixed' ||
      frequency === 'one_off' ||
      effectiveInstallments > 0) &&
    (isAuto ? selectedCardId && startDate : followUpDate);

  const handleSave = async () => {
    try {
      const result = await createPledgeAsync({
        donar_id: selectedDonor.value,
        package_id: packageId?.value || null,
        amount: Number(amount),
        frequency,
        duration_type: frequency === 'one_off' ? 'fixed' : durationType,
        installments: effectiveInstallments,
        total_value: totalValue,
        payment_preference: paymentPreference,
        preferred_contact_method: isAuto ? null : contactMethod,
        follow_up_date: isAuto ? null : followUpDate,
        stripe_payment_method_id: isAuto ? selectedCardId : null,
        start_date: isAuto ? startDate : null,
        // The daily job charges the card when next_due_date arrives
        next_due_date: isAuto ? startDate : null,
        gift_aid_eligible: isAuto ? giftAid : false,
        assigned_to: assignedTo?.value || null,
        priority,
        notes: notes || null,
        source: source || null,
        event_name: source === 'event' ? eventName || null : null,
        event_location: source === 'event' ? eventLocation || null : null,
        event_date: source === 'event' ? eventDate || null : null,
        status: isAuto ? 'payment_scheduled' : 'awaiting_follow_up'
      });

      const pledge = result?.data;
      if (pledge?.id) {
        if (!isAuto) {
          // First follow-up reminder comes from the form
          await createFollowUpAsync({
            pledge_id: pledge.id,
            due_date: followUpDate,
            method: contactMethod,
            notes: contactNotes || null
          }).catch(() => {});
        }
        navigate(parseRouteParams(paths.pledgeProfile, { id: pledge.id }));
      }
    } catch (e) {
      // Error toasts handled by the hooks
    }
  };

  return (
    <>
      {showAddCard && selectedDonor?.value && (
        <AddCardModal
          show={showAddCard}
          onHide={() => setShowAddCard(false)}
          donorId={selectedDonor.value}
          onAdded={handleCardAdded}
        />
      )}
      <Card className="mb-3">
        <Card.Body className="py-3">
          <Flex justifyContent="between" alignItems="center">
            <h4 className="mb-0">Add New Pledge</h4>
            <LoadingButton
              loading={isCreating}
              disabled={!isValid || isCreating}
              onClick={handleSave}
            >
              Save Pledge
            </LoadingButton>
          </Flex>
        </Card.Body>
      </Card>

      {/* Card 1: Donor */}
      <Card className="mb-3">
        <Card.Header className="pb-0">
          <Card.Title className="fw-medium">1. Donor</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Label>Select donor</Form.Label>
              <ReactSelect
                value={selectedDonor}
                onChange={setSelectedDonor}
                options={donorOptions}
                placeholder="Search by name or email..."
                classNamePrefix="react-select"
                isClearable
              />
            </Col>
            <Col md={4}>
              <IconButton
                icon="plus"
                variant="falcon-default"
                size="sm"
                onClick={() => navigate(paths.addNewDonar)}
              >
                New Donor
              </IconButton>
            </Col>
          </Row>
          <small className="text-600">
            Donor not in the list? Create them with the New Donor form first —
            it captures full contact details and lead source — then come back
            and select them here.
          </small>
        </Card.Body>
      </Card>

      {/* Card 2: Pledge details */}
      <Card className="mb-3">
        <Card.Header className="pb-0">
          <Card.Title className="fw-medium">2. Pledge Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Campaign / Appeal</Form.Label>
              <ReactSelect
                value={packageId}
                onChange={handleSelectPackage}
                options={packageOptions}
                placeholder="Select campaign (optional)..."
                classNamePrefix="react-select"
                isClearable
              />
            </Col>
            <Col md={6}>
              <Form.Label>Pledge amount (per payment) *</Form.Label>
              {packageId && packagePresetAmounts.length > 0 ? (
                <>
                  <div className="d-flex flex-wrap gap-2">
                    {packagePresetAmounts.map(preset => (
                      <Button
                        key={preset}
                        variant={
                          !useCustomAmount && Number(amount) === Number(preset)
                            ? 'primary'
                            : 'outline-secondary'
                        }
                        size="sm"
                        className="px-3"
                        onClick={() => {
                          setAmount(String(preset));
                          setUseCustomAmount(false);
                        }}
                      >
                        {formatMoney(Number(preset))}
                      </Button>
                    ))}
                    {packageAllowsCustom && (
                      <Button
                        variant={
                          useCustomAmount ? 'primary' : 'outline-secondary'
                        }
                        size="sm"
                        className="px-3"
                        onClick={() => {
                          setUseCustomAmount(true);
                          setAmount('');
                        }}
                      >
                        Custom
                      </Button>
                    )}
                  </div>
                  {useCustomAmount && (
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      className="mt-2"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      autoFocus
                    />
                  )}
                </>
              ) : (
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Col>
            <Col md={6}>
              <Form.Label>Frequency</Form.Label>
              <Form.Select
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
              >
                {Object.entries(PLEDGE_FREQUENCIES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {frequency !== 'one_off' && (
              <Col md={6}>
                <Form.Label>Duration</Form.Label>
                <Form.Select
                  value={durationType}
                  onChange={e => setDurationType(e.target.value)}
                >
                  <option value="until_cancelled">Until cancelled</option>
                  <option value="fixed">Fixed number of payments</option>
                </Form.Select>
              </Col>
            )}
            {frequency !== 'one_off' && durationType === 'fixed' && (
              <Col md={6}>
                <Form.Label>Number of payments *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={installments}
                  onChange={e => setInstallments(e.target.value)}
                  placeholder={frequency === 'monthly' ? 'e.g. 12' : 'e.g. 3'}
                />
              </Col>
            )}
            <Col md={12}>
              <div className="bg-light rounded p-3">
                <span className="text-600 me-2">Total pledge value:</span>
                <span className="fw-bold fs-1">
                  {totalValue
                    ? formatMoney(totalValue)
                    : amount
                    ? `${formatMoney(Number(amount))}/${
                        frequency === 'annually' ? 'year' : 'month'
                      } until cancelled`
                    : '—'}
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Card 3: Payment preference */}
      <Card className="mb-3">
        <Card.Header className="pb-0">
          <Card.Title className="fw-medium">3. Payment Preference</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="pref-contact"
              name="payment-preference"
              label="Staff follow up — payments recorded manually"
              checked={paymentPreference === 'contact'}
              onChange={() => setPaymentPreference('contact')}
            />
            <Form.Check
              type="radio"
              id="pref-auto"
              name="payment-preference"
              label="Automatic card payment — charge the donor's saved card on each due date"
              checked={paymentPreference === 'auto'}
              onChange={() => setPaymentPreference('auto')}
            />
          </div>

          {paymentPreference === 'contact' ? (
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Preferred contact method</Form.Label>
                <Form.Select
                  value={contactMethod}
                  onChange={e => setContactMethod(e.target.value)}
                >
                  {Object.entries(CONTACT_METHODS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Follow-up date *</Form.Label>
                <Form.Control
                  type="date"
                  value={followUpDate}
                  onChange={e => setFollowUpDate(e.target.value)}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Contact notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={contactNotes}
                  onChange={e => setContactNotes(e.target.value)}
                  placeholder="e.g. Prefers calls after 6pm"
                />
              </Col>
            </Row>
          ) : (
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>First payment date *</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </Col>
              <Col md={8}>
                <Form.Label>Saved card *</Form.Label>
                {!selectedDonor ? (
                  <p className="text-600 fs--1 mb-0 mt-1">
                    Select a donor first to see their saved cards.
                  </p>
                ) : cardsLoading ? (
                  <p className="text-600 fs--1 mb-0 mt-1">Loading cards…</p>
                ) : savedCards.length === 0 ? (
                  <div className="mt-1">
                    <p className="text-warning fs--1 mb-2">
                      This donor has no saved card yet.
                    </p>
                    <Button
                      variant="falcon-primary"
                      size="sm"
                      onClick={() => setShowAddCard(true)}
                    >
                      Add a card
                    </Button>
                  </div>
                ) : (
                  <>
                    <Form.Select
                      value={selectedCardId}
                      onChange={e => setSelectedCardId(e.target.value)}
                    >
                      {savedCards.map(pm => (
                        <option key={pm.id} value={pm.id}>
                          {(pm.card?.brand || 'card').toUpperCase()} ••••{' '}
                          {pm.card?.last4} — expires {pm.card?.exp_month}/
                          {pm.card?.exp_year}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="link"
                      size="sm"
                      className="px-0 fs--1"
                      onClick={() => setShowAddCard(true)}
                    >
                      + Add a different card
                    </Button>
                  </>
                )}
              </Col>
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="pledge-gift-aid"
                  label="Claim Gift Aid on these payments (donor must have a valid declaration)"
                  checked={giftAid}
                  onChange={e => setGiftAid(e.target.checked)}
                />
              </Col>
              <Col md={12}>
                <small className="text-600">
                  The card is charged {formatMoney(Number(amount) || 0)}{' '}
                  {frequency === 'one_off'
                    ? 'once on the first payment date.'
                    : `each ${
                        frequency === 'annually' ? 'year' : 'month'
                      } starting on the first payment date${
                        effectiveInstallments
                          ? `, for ${effectiveInstallments} payments`
                          : ' until the pledge is cancelled'
                      }.`}
                </small>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Card 4: Internal tracking */}
      <Card className="mb-3">
        <Card.Header className="pb-0">
          <Card.Title className="fw-medium">4. Internal Tracking</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Assigned staff</Form.Label>
              <ReactSelect
                value={assignedTo}
                onChange={setAssignedTo}
                options={staffOptions}
                placeholder="Assign to..."
                classNamePrefix="react-select"
                isClearable
              />
            </Col>
            <Col md={4}>
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={priority}
                onChange={e => setPriority(e.target.value)}
              >
                {Object.entries(PLEDGE_PRIORITIES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Source</Form.Label>
              <Form.Select
                value={source}
                onChange={e => setSource(e.target.value)}
              >
                <option value="">Not recorded</option>
                {Object.entries(PLEDGE_SOURCES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {source === 'event' && (
              <>
                <Col md={4}>
                  <Form.Label>Event title</Form.Label>
                  <Form.Control
                    value={eventName}
                    onChange={e => setEventName(e.target.value)}
                    placeholder="e.g. Ramadan Fundraising Dinner"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Event location</Form.Label>
                  <Form.Control
                    value={eventLocation}
                    onChange={e => setEventLocation(e.target.value)}
                    placeholder="Venue / town"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Event date</Form.Label>
                  <Form.Control
                    type="date"
                    value={eventDate}
                    onChange={e => setEventDate(e.target.value)}
                  />
                </Col>
              </>
            )}
            <Col md={12}>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Flex justifyContent="end" className="mb-4">
        <LoadingButton
          size="lg"
          loading={isCreating}
          disabled={!isValid || isCreating}
          onClick={handleSave}
        >
          Save Pledge
        </LoadingButton>
      </Flex>
    </>
  );
};

export default AddPledge;
